import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner, Modal } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import CaseStudyService, { CaseStudy, InstructorCourse } from "../../_services/CaseStudyService";
import CaseEditorModal from "./CaseEditorModal";
import CaseResponsesPanel from "./CaseResponsesPanel";
import "./CaseStudiesPage.css";

interface IncomingContext {
  stepId: number | null;
  lessonPlanId: number | null;
  batchId: number | null;
  stepName: string;
  from: string;
}

function parseContext(search: string): IncomingContext {
  const params = new URLSearchParams(search);
  return {
    stepId: params.get("stepId") ? Number(params.get("stepId")) : null,
    lessonPlanId: params.get("lessonPlanId")
      ? Number(params.get("lessonPlanId"))
      : null,
    batchId: params.get("batchId") ? Number(params.get("batchId")) : null,
    stepName: params.get("stepName") || "",
    from: params.get("from") || "",
  };
}

const CaseStudiesPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const ctx = useMemo(() => parseContext(location.search), [location.search]);
  const initialCaseId = useMemo(() => {
    const v = new URLSearchParams(location.search).get("caseId");
    return v ? Number(v) : null;
  }, [location.search]);
  const [view, setView] = useState<"library" | "responses">(
    new URLSearchParams(location.search).get("view") === "responses" ? "responses" : "library",
  );

  const [cases, setCases] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CaseStudy | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<CaseStudy | null>(null);
  const [autoSavedContext, setAutoSavedContext] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [courses, setCourses] = useState<InstructorCourse[]>([]);
  const [courseMode, setCourseMode] = useState<"instructor" | "admin">("admin");
  const [selectedCourseId, setSelectedCourseId] = useState<number | "">("");
  const [contextCourseId, setContextCourseId] = useState<number | null>(null);

  // Load courses once. Also resolve the incoming step/batch context to a
  // courseId so (a) the library defaults to that course and (b) the editor
  // gets an auto-fill.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await CaseStudyService.listInstructorCourses();
        if (cancelled || !resp.success) return;
        setCourses(resp.data.courses);
        setCourseMode(resp.data.mode);
      } catch {
        // Non-fatal — filter just stays empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ctx.stepId && !ctx.batchId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await CaseStudyService.resolveCourseFromContext({
          stepId: ctx.stepId ?? undefined,
          batchId: ctx.batchId ?? undefined,
        });
        if (cancelled || !resp.success) return;
        const cid = resp.data.courseId;
        if (cid) {
          setContextCourseId(cid);
          setSelectedCourseId(cid);
        }
      } catch {
        // Non-fatal — user picks the course manually.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.stepId, ctx.batchId]);

  useEffect(() => {
    load(search.trim() || undefined, selectedCourseId || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  // If arrived from a flip-class step with context, auto-open the editor for a new case (once).
  useEffect(() => {
    if (ctx.stepId && ctx.stepName && !hasAutoOpened) {
      setEditing(null);
      setEditorOpen(true);
      setHasAutoOpened(true);
    }
  }, [ctx.stepId, ctx.stepName, hasAutoOpened]);

  const load = async (q?: string, courseId?: number | "") => {
    try {
      setLoading(true);
      setError(null);
      const params: { q?: string; courseId?: number } = {};
      if (q) params.q = q;
      if (courseId) params.courseId = Number(courseId);
      const resp = await CaseStudyService.list(
        Object.keys(params).length ? params : undefined,
      );
      if (resp.success) setCases(resp.data.cases);
    } catch (e: any) {
      setError(e?.message || "Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(search.trim(), selectedCourseId || undefined);
  };

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (cs: CaseStudy) => {
    setEditing(cs);
    setEditorOpen(true);
  };

  const handleSaved = async (savedCase: CaseStudy | null) => {
    setEditorOpen(false);
    setEditing(null);
    if (savedCase && ctx.stepId) {
      try {
        await CaseStudyService.attachToStep(ctx.stepId, savedCase.Id);
        setAutoSavedContext(true);
      } catch {
        // Non-fatal — teacher can attach manually from the CaseDiscussionManager.
      }
    }
    load(search.trim() || undefined, selectedCourseId || undefined);
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmingDelete) return;
    try {
      await CaseStudyService.delete(confirmingDelete.Id);
      setConfirmingDelete(null);
      load(search.trim() || undefined, selectedCourseId || undefined);
    } catch (e: any) {
      setError(e?.message || "Failed to delete case");
    }
  };

  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    if (ctx.from === "class-review" && ctx.lessonPlanId && ctx.stepId) {
      const params = new URLSearchParams();
      if (ctx.batchId) params.set("batchId", String(ctx.batchId));
      const qs = params.toString();
      history.push(
        `/lesson-plan/${ctx.lessonPlanId}/step/${ctx.stepId}/class-review${qs ? `?${qs}` : ""}`,
      );
      return;
    }
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push("/home");
  };

  const backLabel = (() => {
    if (ctx.from === "class-review" && ctx.stepName) {
      return `← Back to "${ctx.stepName}"`;
    }
    if (ctx.from === "class-review") return "← Back to Class Review";
    return "← Back";
  })();

  return (
    <div className="case-studies-page">
      <div className="cs-hero">
        <div className="cs-hero-content">
          <a href="#" onClick={handleBack} className="cs-back">
            {backLabel}
          </a>
          <h1 className="cs-title">Case Study Library</h1>
          <div className="cs-subtitle">
            Author and curate cases — the cases here power AI-assisted in-class discussions
          </div>
          {ctx.stepName && (
            <div className="cs-context-badge">
              Authoring for step: <strong>{ctx.stepName}</strong>
              <span className="cs-context-hint">
                {" "}
                — new cases will be auto-attached to this step.
              </span>
            </div>
          )}
        </div>
        <div className="cs-hero-actions">
          <Button variant="light" className="cs-new-btn" onClick={openNew}>
            <span className="cs-new-icon">＋</span> New Case
          </Button>
        </div>
      </div>

      <div className="cs-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === "library"}
          className={`cs-tab ${view === "library" ? "cs-tab-active" : ""}`}
          onClick={() => setView("library")}
        >
          Library
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "responses"}
          className={`cs-tab ${view === "responses" ? "cs-tab-active" : ""}`}
          onClick={() => setView("responses")}
        >
          Responses
        </button>
      </div>

      {view === "responses" ? (
        <div className="cs-body">
          <CaseResponsesPanel
            stepId={ctx.stepId}
            batchId={ctx.batchId}
            initialCaseId={initialCaseId}
          />
        </div>
      ) : (
      <div className="cs-body">
        <Form onSubmit={handleSearch} className="cs-search-row">
          <div className="cs-search-wrap">
            <span className="cs-search-icon">🔍</span>
            <Form.Control
              type="search"
              placeholder="Search by title, scenario text, or concept…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="cs-search-input"
            />
          </div>
          {courses.length > 0 && (
            <Form.Select
              value={selectedCourseId}
              onChange={(e) =>
                setSelectedCourseId(e.target.value ? Number(e.target.value) : "")
              }
              className="cs-course-filter"
              aria-label={courseMode === "instructor" ? "My courses" : "Course"}
            >
              <option value="">
                {courseMode === "instructor" ? "My courses (all)" : "All courses"}
              </option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.shortCode ? `${c.shortCode} — ${c.name}` : c.name}
                </option>
              ))}
            </Form.Select>
          )}
          <Button type="submit" variant="primary" className="cs-search-btn">
            Search
          </Button>
        </Form>

        {error && (
          <div className="cs-error-card">
            <strong>Something went wrong.</strong>
            <div>{error}</div>
          </div>
        )}

        {loading ? (
          <div className="cs-loading">
            <Spinner animation="border" />
            <div>Loading cases…</div>
          </div>
        ) : cases.length === 0 ? (
          <div className="cs-empty">
            <div className="cs-empty-illustration">
              <div className="cs-empty-bubble cs-empty-bubble-1">🧬</div>
              <div className="cs-empty-bubble cs-empty-bubble-2">💡</div>
              <div className="cs-empty-bubble cs-empty-bubble-3">📖</div>
            </div>
            <h2>Your library is empty</h2>
            <p>
              Cases are the spark for AI-led small-group discussions in flip class. Author one good case,
              reuse it across every batch.
            </p>
            <Button variant="primary" size="lg" onClick={openNew} className="cs-empty-cta">
              ＋ Author your first case
            </Button>
          </div>
        ) : (
          <>
            <div className="cs-summary-row">
              <span className="cs-count">
                {cases.length} {cases.length === 1 ? "case" : "cases"}
              </span>
            </div>
            <div className="cs-card-grid">
              {cases.map((cs) => (
                <div key={cs.Id} className="cs-card" onClick={() => openEdit(cs)}>
                  <div className="cs-card-accent" />
                  <div className="cs-card-header">
                    <h3 className="cs-card-title">{cs.Title}</h3>
                    <button
                      type="button"
                      className="cs-card-delete"
                      title="Delete case"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmingDelete(cs);
                      }}
                    >
                      ⋯
                    </button>
                  </div>
                  <div className="cs-card-scenario">
                    {cs.CaseText.length > 220
                      ? `${cs.CaseText.slice(0, 220)}…`
                      : cs.CaseText}
                  </div>
                  {cs.LearningObjectives && (
                    <div className="cs-card-objectives">
                      <span className="cs-card-objectives-label">Objectives</span>
                      <span className="cs-card-objectives-text">
                        {cs.LearningObjectives.length > 100
                          ? `${cs.LearningObjectives.slice(0, 100)}…`
                          : cs.LearningObjectives}
                      </span>
                    </div>
                  )}
                  {cs.KeyConcepts && cs.KeyConcepts.length > 0 && (
                    <div className="cs-card-tags">
                      {cs.KeyConcepts.slice(0, 4).map((c, i) => (
                        <span key={i} className="cs-card-tag">
                          {c}
                        </span>
                      ))}
                      {cs.KeyConcepts.length > 4 && (
                        <span className="cs-card-tag-more">+{cs.KeyConcepts.length - 4}</span>
                      )}
                    </div>
                  )}
                  <div className="cs-card-footer">
                    <span className="cs-card-meta">
                      Updated {cs.UpdatedAt ? new Date(cs.UpdatedAt).toLocaleDateString() : "—"}
                    </span>
                    <span className="cs-card-edit-hint">Edit →</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      )}

      <CaseEditorModal
        isOpen={editorOpen}
        existing={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
        initialAiTopic={!editing ? ctx.stepName : ""}
        autoOpenAi={!editing && !!ctx.stepName}
        courses={courses}
        courseMode={courseMode}
        initialCourseId={!editing ? contextCourseId : null}
      />

      <Modal
        show={!!confirmingDelete}
        onHide={() => setConfirmingDelete(null)}
        centered
        dialogClassName="cs-confirm-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete case?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will remove <strong>{confirmingDelete?.Title}</strong> from the library. Existing
          discussions that already used it will keep their transcripts.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmingDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirmed}>
            Delete case
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CaseStudiesPage;
